import { apiFetch } from "./api";

/**
 * ADR-0133 Amendment 12 — what a NEW step of each type looks like is substrate,
 * owned by the hub. This module is a READER, not a second author.
 *
 * Before this, the console seeded a valid starter config at the palette gesture
 * while the MCP door stored `{}` — the same intent produced two different
 * objects, and the door's version often failed silently (a spec-less KPI card
 * ran to SUCCESS and rendered blank). The shapes now live in one place
 * (`ops/nodeAuthoringContracts.ts` on the hub) and reach this surface over HTTP;
 * a local copy here is exactly the drift being removed, so there deliberately
 * is not one.
 *
 * Fallback, deliberate: if the shapes are unavailable, a new step is created
 * with an EMPTY config rather than a guessed one. That state is now loudly
 * reported by the validator and rendered at the node, so it is recoverable —
 * whereas a stale local copy would be silently wrong, which is the failure mode
 * this whole change exists to end.
 */

export type ExpectedPendingEntry = { code: string; field?: string; note: string };

/** ADR-0154 §8.3 — per-node-type membership of the hub's node-type sets. */
export type NodeTypeSets = { executable: boolean; schedulable: boolean };

type ShapesResponse = {
  shapes: Record<string, Record<string, unknown>>;
  /** ADR-0153 Phase 5 — per-type declared unfinished-by-design findings. */
  expected_pending?: Record<string, ExpectedPendingEntry[]>;
  /** ADR-0154 §8.3 — which types are executable / schedulable, from the owning consts. */
  sets?: Record<string, NodeTypeSets>;
};
type Fetcher = () => Promise<ShapesResponse>;

let cache: Record<string, Record<string, unknown>> | null = null;
let pendingCache: Record<string, ExpectedPendingEntry[]> = {};
let setsCache: Record<string, NodeTypeSets> = {};
let inFlight: Promise<void> | null = null;

const defaultFetcher: Fetcher = () => apiFetch<ShapesResponse>("/api/tenant/node-creation-shapes");

/**
 * Load the shapes once per session. Safe to call from several mounts: concurrent
 * callers share one request, and a failure clears the in-flight promise so a
 * later mount can retry rather than being stuck with the first failure.
 */
export async function loadNodeCreationShapes(fetcher: Fetcher = defaultFetcher): Promise<void> {
  if (cache) return;
  if (inFlight) return inFlight;
  inFlight = fetcher()
    .then((res) => {
      cache = res.shapes ?? {};
      pendingCache = res.expected_pending ?? {};
      setsCache = res.sets ?? {};
    })
    .catch(() => {
      // Swallowed on purpose: an unreachable shape service must not block the
      // editor from opening. `nodeCreationShape` degrades to {} and the empty
      // config is reported by the validator like any other incomplete step.
      cache = null;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** The canonical starting config for a new step of this type, as a fresh copy. */
export function nodeCreationShape(nodeType: string): Record<string, unknown> {
  const shape = cache?.[nodeType];
  return shape ? (structuredClone(shape) as Record<string, unknown>) : {};
}

/**
 * ADR-0154 §6.3 C4 — is this node type schedulable? Answered by the hub, which
 * owns the set.
 *
 * The console used to keep its own list, and that list was missing
 * `model_train` — so a whole node type could not be picked as a schedule target
 * from the canvas even though the hub allowed it. Unknown types answer `false`
 * rather than guessing: a type the hub has not described is one this build
 * knows nothing about, and offering it would be the same mistake inverted.
 */
export function isSchedulableNodeType(nodeType: string): boolean {
  return setsCache[nodeType]?.schedulable === true;
}

/** ADR-0154 §6.3 C4 — is this node type runnable via execute-preview? */
export function isExecutableNodeType(nodeType: string): boolean {
  return setsCache[nodeType]?.executable === true;
}

/** True once the hub's shapes are in hand — the editor uses this to explain a degraded create. */
export function nodeCreationShapesLoaded(): boolean {
  return cache !== null;
}

/**
 * The declared unfinished-by-design finding when this finding matches one, else
 * null. Same matcher semantics as the hub's `expectedPendingMatch` (code
 * equality; declared field matches the finding field by suffix) — kept in
 * lockstep by the copy invariants in operationFindings.test.ts.
 */
export function expectedPendingFor(
  nodeType: string,
  finding: { code: string; field: string | null }
): { note: string } | null {
  const declared = pendingCache[nodeType];
  if (!declared) return null;
  for (const entry of declared) {
    if (entry.code !== finding.code) continue;
    if (entry.field !== undefined) {
      if (!finding.field || !finding.field.endsWith(entry.field)) continue;
    }
    return { note: entry.note };
  }
  return null;
}

export function resetNodeCreationShapesForTests(): void {
  cache = null;
  pendingCache = {};
  inFlight = null;
}
